import React, { SyntheticEvent } from "react";
import { Typeahead } from "react-bootstrap-typeahead";

import { Keys } from "@utils/enums";
import { sortBy } from "@utils/general";
import { cleanUp } from "@utils/textUtils";

import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";
import { clearMessages } from "@redux/modules/value/messages/messages.utils";

import DropDownMenu from "./dropDownMenu/dropDownMenu.component";

type Props = {
    name?: string;
    storesList: Array<StoreDetailsModel>;
    disabled: boolean;
    searchQuery: string;
    shouldFocus: boolean;
    onEnter?: (event: SyntheticEvent<HTMLInputElement>, cb: () => void) => void;
    onChange: (
        searchQuery: string,
        fromInput?: boolean,
        value?: StoreDetailsModel,
    ) => void;
    onSearch: (store: any) => void;
    className?: string;
    placeholder?: string;
    allowNew?: boolean;
};

const StorePickerAutoComplete = ({
    name,
    storesList,
    disabled,
    searchQuery,
    onEnter,
    onChange,
    onSearch,
    shouldFocus,
    className,
    placeholder,
    allowNew = true,
}: Props) => {
    const [isAutoCompleteOpen, setIsAutoCompleteOpen] = React.useState(false);
    const typeAheadRef = React.createRef<any>();

    React.useEffect(() => {
        return function cleanUp() {
            setIsAutoCompleteOpen(false);
        };
    }, []);

    React.useEffect(() => {
        if (
            !searchQuery &&
            typeAheadRef.current &&
            typeAheadRef.current.getInput() &&
            shouldFocus
        ) {
            typeAheadRef.current.getInput().focus();
        }
    });

    const mappedStoreList = storesList
        .filter(s => s.activeStore && s.storeName)
        .map(s => ({
            ...s,
            label: s.storeName,
            postalCode: s.address?.postalCode || "",
        }))
        .sort(sortBy("label"));

    const selectedItem = mappedStoreList.find(s => s.label === searchQuery);

    return (
        <Typeahead
            placeholder={placeholder}
            name={name}
            id="searchQuery"
            className={className}
            autoFocus={false}
            open={isAutoCompleteOpen}
            defaultInputValue={searchQuery}
            paginate={false}
            options={mappedStoreList}
            disabled={disabled}
            allowNew={results => results.length === 0 && allowNew}
            filterBy={(option: any, { text }) =>
                !!~cleanUp(option.label, true)
                    .toLowerCase()
                    .indexOf(cleanUp(text, true).toLowerCase()) ||
                !!~option.postalCode.toLowerCase().indexOf(text.toLowerCase())
            }
            selected={selectedItem ? [selectedItem] : []}
            ref={typeAheadRef}
            onBlur={() => {
                setIsAutoCompleteOpen(false);
            }}
            onFocus={() => {
                if (searchQuery) {
                    setIsAutoCompleteOpen(true);
                }
            }}
            inputProps={{
                // @ts-ignore
                "data-test": "storePickerAutoComplete-input",
                onKeyUp: e => {
                    if (e.keyCode === Keys.ENTER) {
                        onEnter &&
                            onEnter(e, () => setIsAutoCompleteOpen(false));
                    }
                },
            }}
            onInputChange={val => {
                setIsAutoCompleteOpen(true);
                onChange(val, true);
            }}
            onChange={(val: any) => {
                // due to multiselect being an option, we always take the first value
                if (val[0]) {
                    setIsAutoCompleteOpen(false);
                    onChange(val[0].label, false, val[0]);
                    onSearch(val[0]);
                    if (typeAheadRef.current) {
                        typeAheadRef.current.getInput().blur();
                    }
                }
                clearMessages();
            }}
            renderMenu={(results, menuProps, props) => (
                <DropDownMenu
                    results={results || []}
                    menuProps={menuProps}
                    listProps={props}
                />
            )}
            selectHint={(_, e) => {
                return e.keyCode === Keys.ENTER;
            }}
        />
    );
};

export { StorePickerAutoComplete };
